import type { DateRangePicker } from 'ui/date-range-picker/date-range-picker';

import { TickUnit } from 'cadenza/conditions/model/tick-unit';
import type { StepsConfig } from 'cadenza/conditions/date-time-condition/steps-config';
import { addTickUnits, differenceInTickUnit } from 'cadenza/conditions/date-time-condition/date-time-condition-utils';
import {
  AnimationVisualization,
  type OversetInfo
} from 'cadenza/conditions/animation/animation-visualization';

import i18n from './date-animation-preview.properties';

const COMPONENT_NAME = 'd-date-animation-preview';

export class DateAnimationPreview extends HTMLElement {

  _stepUnit: TickUnit;
  _timeWindow: number;
  _overlap: number;

  /**
   * The number of frames the animation would have with the given settings if there were no
   * maxFrameNum
   */
  _unlimitedFrameNum: number;

  /** Number of animation frames after applying the frame limit */
  _limitedFrameNum: number;

  _hasIncompleteFrameAtTheEnd: boolean;

  /** Label for complete frames */
  _frameLabel: string;

  /**
   * Label for the part of the data that is before the end date but still won't get animated
   * of the frame limit
   */
  _undersetLabel?: string;

  _overset?: OversetInfo;

  _animationRangePicker: DateRangePicker;

  constructor (
    stepsConfig: StepsConfig,
    minTickUnit: TickUnit,
    start: Date,
    end: Date,
    maxFrames: number,
    animationRangePicker: DateRangePicker) {
    super();
    this._animationRangePicker = animationRangePicker;

    this._stepUnit = stepsConfig.unit;
    this._timeWindow = stepsConfig.timeWindow;
    this._overlap = stepsConfig.overlap;

    const nonOverlappingPartInStepsUnit = stepsConfig.timeWindow - stepsConfig.overlap;
    this._unlimitedFrameNum = 1;
    let lastStepStart = start;
    // not corrected yet for the fact that the back-end "fills up" units smaller than the minTickUnit
    let rawCompleteLastStepEnd = addTickUnits(start, stepsConfig.timeWindow, stepsConfig.unit);
    let rawCompleteSecondLastStepEnd = rawCompleteLastStepEnd;
    while (rawCompleteLastStepEnd.getTime() < addTickUnits(end, 1, minTickUnit).getTime()) {
      this._unlimitedFrameNum++;
      rawCompleteSecondLastStepEnd = rawCompleteLastStepEnd;
      lastStepStart = addTickUnits(lastStepStart, nonOverlappingPartInStepsUnit, stepsConfig.unit);
      rawCompleteLastStepEnd =
          addTickUnits(rawCompleteLastStepEnd, nonOverlappingPartInStepsUnit, stepsConfig.unit);
    }
    const completeLastStepEnd = addTickUnits(rawCompleteLastStepEnd, -1, minTickUnit);

    // use end + 1 and rawCompleteLastStepEnd instead of end and completeLastStepEnd, so that, if we are
    // stepping in whole months, we calculate the difference from the 1st of a month to the 1st of
    // the next month (resulting in a difference of one month), instead of e.g. from the 31st of a
    // month to the 30th of the next month (resulting in a difference of 0 months).
    const oversetInMinTickUnit = differenceInTickUnit(
      addTickUnits(end, 1, minTickUnit),
      rawCompleteLastStepEnd,
      minTickUnit);

    const completeLastStepDurationInMinTickUnits =
        differenceInTickUnit(lastStepStart, rawCompleteLastStepEnd, minTickUnit);
    const oversetToCompleteFrameRatio =
        oversetInMinTickUnit / completeLastStepDurationInMinTickUnits;

    const secondLastFrameEndToLastFrameEndInMinTickUnits =
        differenceInTickUnit(
          rawCompleteSecondLastStepEnd,
          addTickUnits(end, 1, minTickUnit),
          minTickUnit);
    const lastFramesEndDistToCompleteFrameRatio =
        secondLastFrameEndToLastFrameEndInMinTickUnits / completeLastStepDurationInMinTickUnits;

    this._limitedFrameNum = Math.min(this._unlimitedFrameNum, maxFrames);
    this._hasIncompleteFrameAtTheEnd =
        this._unlimitedFrameNum === this._limitedFrameNum
        && completeLastStepEnd.getTime() !== end.getTime();

    this._frameLabel = `${this._timeWindow} ${TickUnit.i18n(this._stepUnit, { plural: this._timeWindow > 1 })}`;
    this._undersetLabel = undersetLabel(start, end, stepsConfig, minTickUnit, this._limitedFrameNum);
    if (this._hasIncompleteFrameAtTheEnd) {
      this._overset = {
        incompleteFrameLabel: formatDuration(lastStepStart, end, stepsConfig.unit, minTickUnit, true),
        oversetLabel: formatDuration(
          addTickUnits(end, 1, minTickUnit),
          completeLastStepEnd,
          stepsConfig.unit,
          minTickUnit,
          true),
        oversetToCompleteFrameRatio,
        lastFramesEndDistToCompleteFrameRatio
      };
    }

    if (this._limitedFrameNum === 2) {
      // With only 2 frames there is no space for a label
      this._animationRangePicker.updateLabelBetweenStartAndEnd('');
    } else {
      this._animationRangePicker.updateLabelBetweenStartAndEnd(
        `${this._limitedFrameNum}/${maxFrames} ${i18n('frames')}`,
        this._limitedFrameNum + '/' + maxFrames
      );
    }
  }

  connectedCallback () {
    this._render();
  }

  hasOverset () {
    return !!this._overset;
  }

  hasLessThanTwoFrames () {
    return this._limitedFrameNum < 2;
  }

  _render () {
    this.classList.add(COMPONENT_NAME);
    this.innerHTML = '';
    this.append(new AnimationVisualization({
      numberOfFrames: this._limitedFrameNum,
      frameLabels: this._frameLabel,
      windowSize: this._timeWindow,
      overlap: this._overlap,
      undersetLabel: this._undersetLabel,
      overset: this._overset,
      rangePicker: this._animationRangePicker
    }));
  }

}

function undersetLabel (
  start: Date,
  end: Date,
  stepsConfig: StepsConfig,
  minTickUnit: TickUnit,
  numberOfFrames: number): string | undefined {
  // not corrected yet for the fact that the back-end "fills up" units smaller than the minTickUnit
  const rawStepsEndTime = addTickUnits(
    start,
    numberOfFrames * (stepsConfig.timeWindow - stepsConfig.overlap) + stepsConfig.overlap,
    stepsConfig.unit);

  if (rawStepsEndTime.getTime() >= end.getTime()) {
    return undefined;
  }

  return formatDuration(rawStepsEndTime, end, stepsConfig.unit, minTickUnit);
}

/**
 * For creating the labels of underset, incomplete frame and overset.
 *
 * Not usable for formatting arbitrary durations as it contains special logic for compensating for
 * the backend (that generates the animation) "filling up" date units smaller than the minTickUnit.
 *
 * @param start - start of the duration
 * @param end - end of the duration
 * @param stepUnit - unit that the animation steps are defined in
 * @param minTickUnit - smallest unit of the date format of the animated condition
 * @param shortFormat - whether to use a short format, e.g. "d" instead of "Day" or "Days"
 * @return human-readable duration
 * @throws if neither a complete preferredUnit nor a complete fallbackUnit fit into the
 * duration defined by start and end
 */
function formatDuration (
  start: Date,
  end: Date,
  stepUnit: TickUnit,
  minTickUnit: TickUnit,
  shortFormat = false): string {
  // Correct for the fact that the back-end "fills up" units smaller than the minTickUnit.
  // We add 1 minTickUnit to the end instead of subtracting 1 minTickUnit from the start so that,
  // if we are stepping in whole months, we calculate the difference from the 1st of a month to the
  // 1st of the next month (resulting in a difference of one month), instead of e.g. from the 31st
  // of a month to the 30th of the next month (resulting in a difference of 0 months)
  const adjustedEnd = addTickUnits(end, 1, minTickUnit);

  const differenceInStepUnit = differenceInTickUnit(start, adjustedEnd, stepUnit);
  const differenceInMinTickUnit = differenceInTickUnit(start, adjustedEnd, minTickUnit);

  const endTimeCalculatedWithUndersetInStepsUnit = addTickUnits(start, differenceInStepUnit, stepUnit);
  const endTimeCalculatedWithUndersetInMinTickUnit =
      addTickUnits(start, differenceInMinTickUnit, minTickUnit);
  const stepsUnitFitsExactlyInUnderset = endTimeCalculatedWithUndersetInMinTickUnit.getTime()
      === endTimeCalculatedWithUndersetInStepsUnit.getTime();

  if (differenceInStepUnit > 0 && stepsUnitFitsExactlyInUnderset) {
    return differenceInStepUnit + ' ' + TickUnit.i18n(stepUnit, { plural: differenceInStepUnit > 1, shortFormat });
  } else if (differenceInMinTickUnit > 0) {
    return differenceInMinTickUnit + (shortFormat ? '' : ' ')
        + TickUnit.i18n(minTickUnit, { plural: differenceInMinTickUnit > 1, shortFormat });
  } else {
    throw new Error('Neither a complete preferredUnit nor a complete fallbackUnit fit into the'
        + ' duration defined by start and end.');
  }
}

customElements.define(COMPONENT_NAME, DateAnimationPreview);
